import { z } from 'zod';
import { RARITIES } from '../data/items.js';
/**
 * Loot tables: each entry is rolled independently, so a lucky kill can drop
 * two things and an unlucky one nothing. Data (`mobs.ts`), not code.
 */
export const LootEntrySchema = z
    .object({
    templateId: z.string().min(1),
    /** Probability of dropping at all, 0–1. */
    chance: z.number().min(0).max(1),
    min: z.number().int().min(1).default(1),
    max: z.number().int().min(1).default(1),
})
    .refine((entry) => entry.max >= entry.min, 'max must be at least min');
export function rollLoot(table, rng) {
    const drops = [];
    for (const entry of table) {
        if (rng() >= entry.chance)
            continue;
        const quantity = entry.min + Math.floor(rng() * (entry.max - entry.min + 1));
        drops.push({ templateId: entry.templateId, quantity });
    }
    return drops;
}
/**
 * At most one drop: a single roll walks the entries in order, each taking a
 * slice of [0, 1) the size of its chance, and the rest is nothing. The chances
 * must therefore sum to 1 at most. `roll` lets a caller hand over what is left
 * of a roll it has already used for something rarer.
 */
export function rollOneOf(table, rng, roll = rng()) {
    let left = roll;
    for (const entry of table) {
        if (left < entry.chance) {
            const quantity = entry.min + Math.floor(rng() * (entry.max - entry.min + 1));
            return { templateId: entry.templateId, quantity };
        }
        left -= entry.chance;
    }
    return undefined;
}
export function totalChance(table) {
    return table.reduce((sum, entry) => sum + entry.chance, 0);
}
/** Expected drops of one entry per kill, for tuning and for the distribution test. */
export function expectedQuantity(entry) {
    return entry.chance * ((entry.min + entry.max) / 2);
}
/**
 * How a source hands out equipment (`data/loot.ts`): a chance to drop a piece
 * at all, then a rarity drawn by weight. The piece itself comes from the pool
 * of the place — the biome's band of levels — so what drops is worth wearing
 * there, and nowhere near the capital.
 */
export const LootProfileSchema = z
    .object({
    /** Probability of one piece per kill or chest, 0–1. */
    chance: z.number().min(0).max(1),
    /** Relative weights, not percentages: only their ratios matter. */
    weights: z.object({
        common: z.number().min(0),
        rare: z.number().min(0),
        epic: z.number().min(0),
        legendary: z.number().min(0),
    }),
})
    .refine((profile) => RARITIES.some((rarity) => profile.weights[rarity] > 0), 'at least one rarity must weigh something');
export function rollRarity(weights, rng) {
    const total = RARITIES.reduce((sum, rarity) => sum + weights[rarity], 0);
    let roll = rng() * total;
    for (const rarity of RARITIES) {
        roll -= weights[rarity];
        if (roll < 0 && weights[rarity] > 0)
            return rarity;
    }
    // Only reached through rounding on the last weighted rarity.
    return [...RARITIES].reverse().find((rarity) => weights[rarity] > 0) ?? 'common';
}
/**
 * The rarity actually given when the pool has none of the one rolled: the
 * next one down first — a lucky roll in a place without legendaries still
 * gives its best — then up, so a pool with only rares still drops them.
 */
export function availableRarity(pool, rolled) {
    const rank = RARITIES.indexOf(rolled);
    const order = [...RARITIES.slice(0, rank + 1).reverse(), ...RARITIES.slice(rank + 1)];
    return order.find((rarity) => pool[rarity].length > 0);
}
/** At most one piece of equipment, from the pool, under the profile's odds. */
export function rollEquipment(profile, pool, rng) {
    if (rng() >= profile.chance)
        return undefined;
    const rarity = availableRarity(pool, rollRarity(profile.weights, rng));
    if (rarity === undefined)
        return undefined;
    const choices = pool[rarity];
    const templateId = choices[Math.floor(rng() * choices.length)];
    return templateId === undefined ? undefined : { templateId, quantity: 1 };
}
/**
 * One of `entries`, each as likely as its weight: a set whose pieces are not
 * all as rare (Shefira's sword, `rollRaidLoot`).
 */
export function pickWeighted(entries, rng) {
    const total = entries.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0);
    if (total <= 0)
        return undefined;
    let roll = rng() * total;
    for (const entry of entries) {
        if (entry.weight <= 0)
            continue;
        roll -= entry.weight;
        if (roll < 0)
            return entry.value;
    }
    // Only reached through rounding: the last weighted entry.
    return [...entries].reverse().find((entry) => entry.weight > 0)?.value;
}
//# sourceMappingURL=loot.js.map